import { computed, ref } from "vue";
import { filterItems } from "#/doctor_finder/omnisearch-quantum-items";
import { useOmnisearchStore } from "#ce/apps/shared/store/omnisearch-store";
import { emptyToUndefined } from "#ce/utils/props";

// The four search params the Provider panel owns. The doctor-finder bridge reads
// these off the element's exposed `filterNames` so it knows one control carries
// all four (see wizard_step_2.coffee.erb).
export const PROVIDER_FILTER_NAMES = ["new_patient", "name", "provider_type", "affiliation"];

// The typeahead caps how many provider matches it renders; the scan stops at this
// cap rather than walking the whole list per keystroke. Shared with the
// suggestion list's `max-results` so the cap is one contract.
export const PROVIDER_RESULTS_LIMIT = 50;

// The Provider (name) facet is a free-text physician/location name search over
// the shared omnisearch store's `provider` item set. Unlike the specialty facet
// it is a combobox: typing applies the `name` filter, while picking a specific
// provider navigates to that provider's page.

// Resolve a selected provider node to an action: navigate to a specific
// provider's page (url) or apply the typed name as a filter value.
export function providerSelection(item) {
  const payload = item?.payload ?? {};

  if (payload.url) {
    return { type: "navigate", url: payload.url };
  }

  return { type: "filter", value: payload.query ?? item?.title ?? "" };
}

export const useProviderFilter = ({ store, uuid, searchQuery } = {}) => {
  const resolved = store ?? useOmnisearchStore(uuid);

  const providerItems = computed(() => resolved.provider.value.searchItems);

  const query = searchQuery ?? ref("");

  // Providers are only surfaced while typing; idle shows nothing. The list shows
  // matching providers only (no free-text row) — typing and closing already
  // commits the query verbatim as the `name` filter.
  const models = computed(() => {
    const value = query.value.trim();
    if (!value) {
      return [];
    }

    return filterItems(providerItems.value, value, { limit: PROVIDER_RESULTS_LIMIT });
  });

  return {
    loading: resolved.loading,
    error: resolved.error,
    providerItems,
    models,
  };
};

// Owns the Provider panel's four sub-selections and projects them into a single
// `FormData` model — the same multi-value shape DistanceFilter uses — so one
// element can emit all four params. Empty selections are omitted so the bridge
// only ever sees active filters. Restore (deep-link / back-forward) flows through
// `setFilterValues`; clearing the chip flows through `clear`.
export const useProviderFilterModel = (props = {}) => {
  const newPatient = ref(initialValue(props.newPatient));
  const name = ref(emptyToUndefined(props.name));
  const providerType = ref(emptyToUndefined(props.providerType));
  const affiliation = ref(emptyToUndefined(props.affiliation));

  const model = computed(() => {
    const formData = new FormData();

    if (newPatient.value) {
      formData.set("new_patient", newPatient.value);
    }
    if (name.value) {
      formData.set("name", name.value);
    }
    if (providerType.value) {
      formData.set("provider_type", providerType.value);
    }
    if (affiliation.value) {
      formData.set("affiliation", affiliation.value);
    }

    return formData;
  });

  // Apply only the params present in the object so a partial restore (e.g. just
  // `name` in the URL) leaves the other selections untouched.
  function setFilterValues(values = {}) {
    if ("new_patient" in values) {
      // The bridge round-trips a removed param as null. For this filter that is
      // the explicit "Any" option (""), not "no selection" (undefined) —
      // otherwise choosing "Any" clears the param, round-trips back as null, and
      // deselects every radio until a second click.
      newPatient.value = values.new_patient == null ? "" : String(values.new_patient);
    }
    if ("name" in values) {
      name.value = emptyToUndefined(values.name);
    }
    if ("provider_type" in values) {
      providerType.value = emptyToUndefined(values.provider_type);
    }
    if ("affiliation" in values) {
      affiliation.value = emptyToUndefined(values.affiliation);
    }
  }

  function clear() {
    // new_patient clears to the explicit "Any" choice ("") so the radio group
    // keeps a selection; the other free-text facets clear to undefined.
    newPatient.value = "";
    name.value = undefined;
    providerType.value = undefined;
    affiliation.value = undefined;
  }

  return {
    newPatient,
    name,
    providerType,
    affiliation,
    model,
    filterNames: PROVIDER_FILTER_NAMES,
    setFilterValues,
    clear,
  };
};

// new_patient defaults to '' = "Any" (an explicit choice that emits no param) so
// the radio group always renders a selection; a provided value is stringified.
function initialValue(value) {
  return value == null ? "" : String(value);
}
